'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  toasterApi,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { useAppTargetId } from '../../../store/projectStore';
import { useEndpointTabs } from '../../../store/layoutStore';
import { useReports } from '../../../hooks/useReports';
import {
  getCreateReportDialogPayload,
  type CreateReportDialogTrigger,
} from '../create-report-dialog-selection';
import { useReportUnpublishedConfirm } from '../use-report-unpublished-confirm';
import type { FindingsSelectionModel } from './useFindingsSelection';
import { useFindingsBulkConflictToast } from './useFindingsBulkConflictToast';

export function useFindingsReportDialog({
  selectionModel,
  loadedFindings,
}: {
  selectionModel: Pick<
    FindingsSelectionModel,
    'selection' | 'selectedCount' | 'buildBulkFilterPayload'
  >;
  loadedFindings: readonly InternalEnrichedFinding[];
}) {
  const t = useTranslations('proxy.findings.reportActions');
  const tFindingsRow = useTranslations('findingsTab.row');
  const appTargetId = useAppTargetId();
  const queryClient = useQueryClient();
  const { setTab } = useEndpointTabs('app-overview');
  const toastBulkConflict = useFindingsBulkConflictToast();
  const [trigger, setTrigger] = useState<CreateReportDialogTrigger | null>(
    null,
  );
  const { selection, selectedCount, buildBulkFilterPayload } = selectionModel;
  const { reports, isLoading: isLoadingReports } = useReports({
    applicationId: appTargetId,
    enabled: trigger !== null && !!appTargetId,
  });
  const payload = useMemo(
    () => getCreateReportDialogPayload({ trigger, selection, selectedCount }),
    [selectedCount, selection, trigger],
  );
  const unpublishedConfirm = useReportUnpublishedConfirm({
    selection,
    loadedFindings,
    openReportDialog: setTrigger,
  });
  const closeDialog = useCallback(() => setTrigger(null), []);
  const invalidateReports = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['reports', appTargetId] }),
    [appTargetId, queryClient],
  );
  const { openWithConfirm } = unpublishedConfirm;
  const openForFinding = useCallback(
    (finding: InternalEnrichedFinding) =>
      openWithConfirm({
        trigger: { source: 'row', findingId: finding.id },
        rowFinding: finding,
      }),
    [openWithConfirm],
  );
  const openForSelection = useCallback(
    () => openWithConfirm({ trigger: { source: 'toolbar' } }),
    [openWithConfirm],
  );
  const handleReportComplete = useCallback(async () => {
    toasterApi.success({
      title: t('reportGeneratedTitle'),
      description: t('reportGeneratedDescription'),
    });
    closeDialog();
    await invalidateReports();
    setTab('reports');
  }, [closeDialog, invalidateReports, setTab, t]);
  const addToSelectionByFilter = useCallback(
    async (reportId: string): Promise<boolean> => {
      const response = await terraProxyApiClient.addFindingsToReportByFilter(
        reportId,
        buildBulkFilterPayload(),
      );

      if (response.status === 409) {
        toastBulkConflict({
          body: response.body,
          tooManyTitleKey: 'addToReport',
        });

        return false;
      }

      if (response.status !== 200) {
        throw new Error(t('addFailedDescription'));
      }

      return true;
    },
    [buildBulkFilterPayload, t, toastBulkConflict],
  );
  const handleAddToExistingReport = useCallback(
    async (reportId: string) => {
      if (!appTargetId) return false;

      try {
        if (trigger?.source === 'row') {
          await terraProxyApiClient.addFindingsToReport(
            reportId,
            [trigger.findingId],
            appTargetId,
          );
          toasterApi.success({
            title: tFindingsRow('addedToReportTitle'),
            description: tFindingsRow('addedToReportDescription'),
          });
        } else {
          const didAdd =
            selection.mode === 'ids'
              ? await terraProxyApiClient
                  .addFindingsToReport(
                    reportId,
                    Array.from(selection.ids),
                    appTargetId,
                  )
                  .then(() => true)
              : await addToSelectionByFilter(reportId);

          if (!didAdd) {
            return false;
          }

          toasterApi.success({
            title: t('addedTitle'),
            description: t('addedDescription', { count: selectedCount }),
          });
        }

        closeDialog();
        await invalidateReports();

        return true;
      } catch (error) {
        console.error('Failed to add findings to report:', error);
        toasterApi.error({
          title: t('addFailedTitle'),
          description: t('addFailedDescription'),
        });

        return false;
      }
    },
    [
      addToSelectionByFilter,
      appTargetId,
      closeDialog,
      invalidateReports,
      selectedCount,
      selection,
      t,
      tFindingsRow,
      trigger,
    ],
  );

  return {
    openForFinding,
    openForSelection,
    unpublishedConfirm,
    createDialogProps: {
      isOpen: trigger !== null,
      onOpenChange: (open: boolean) => {
        if (!open) {
          closeDialog();
        }
      },
      selectedFindingsCount: payload.count,
      selectedFindingIds: payload.ids,
      applicationId: appTargetId ?? '',
      onComplete: handleReportComplete,
      onAddToExisting: handleAddToExistingReport,
      existingReports: reports,
      isLoadingReports,
      canCreateNew: payload.canCreateNew,
    },
  };
}
